import {
  buildCamerasRequestBodyWithVpnTunnel,
  buildScreenshotRequestBody,
  coercePort,
  isDashcamRequestCancelled,
  mapDashcamHttpError,
  postScreenshot,
} from "../../../../services/capabilitiesService";
import installationToVideoRecorderShape from "./installationToVideoRecorderShape";
import { cloudDeviceHost } from "./probeInstallationViaDashcam";

export const MAX_CHANNELS = 32;
// Max channel grabs in flight, to avoid bursting past Dashcam's rate limit.
const SCREENSHOT_CONCURRENCY = 4;
// Bounds individual channel numbers so "1-99999999" doesn't allocate forever.
const MAX_CHANNEL_NUMBER = 4096;

// Parse "1,3-5,8" → [1,3,4,5,8]; null if malformed or any value is out of [1, MAX_CHANNEL_NUMBER].
export function parseChannelRanges(input) {
  if (typeof input !== "string" || !input.trim()) return null;
  const out = [];
  let malformed = false;
  input.split(",").forEach((piece) => {
    if (malformed) return;
    const p = piece.trim();
    if (/^\d+$/.test(p)) {
      const n = Number(p);
      if (n < 1 || n > MAX_CHANNEL_NUMBER) {
        malformed = true;
        return;
      }
      out.push(n);
      return;
    }
    const m = p.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!m) {
      malformed = true;
      return;
    }
    const start = Number(m[1]);
    const end = Number(m[2]);
    if (start < 1 || start > end || end > MAX_CHANNEL_NUMBER) {
      malformed = true;
      return;
    }
    for (let n = start; n <= end; n += 1) out.push(n);
  });
  if (malformed) return null;
  return [...new Set(out)].sort((a, b) => a - b);
}

// Returns a user-facing reason the scan can't run, or null. Cloud reaches the DVR
// via DDNS/WAN; on-prem tunnels through the inference server (needs server_name).
export function manualScanBlocker(installation, dvr) {
  if (!dvr?.dvr_login) return "Missing DVR login.";
  if (!dvr?.dvr_password) return "Missing DVR password.";
  if (installation?.is_cloud) {
    if (!cloudDeviceHost(installation)) return "Missing DDNS hostname / WAN IP.";
    if (coercePort(dvr?.dvr_external_rtsp_port) == null) return "Missing DVR external RTSP port.";
  } else {
    if (!installation?.server_name) return "Installation has no server assigned.";
    if (!dvr?.dvr_ipv4) return "Missing DVR IPv4.";
    if (coercePort(dvr?.dvr_rtsp_port) == null) return "Missing DVR RTSP port.";
  }
  return null;
}

// Returns `{channel_number: data_uri}`; chunks above MAX_CHANNELS per scan call.
export async function fetchChannelThumbnails({ installation, dvr, channelNumbers, signal }) {
  if (!Array.isArray(channelNumbers) || channelNumbers.length === 0) return {};
  const chunkSize = MAX_CHANNELS;
  const chunks = [];
  for (let i = 0; i < channelNumbers.length; i += chunkSize) {
    chunks.push(channelNumbers.slice(i, i + chunkSize));
  }
  const thumbnails = {};
  // Sequential to respect signal.aborted between chunks.
  for (let i = 0; i < chunks.length; i += 1) {
    if (signal?.aborted) break;

    const { screenshots = [] } = await scanInstallationChannels({ installation, dvr, channelNumbers: chunks[i] });
    screenshots.forEach((shot) => {
      const number = shot?.[0];
      const dataUri = shot?.[2];
      if (Number.isInteger(number) && typeof dataUri === "string" && dataUri.length > 0) {
        thumbnails[number] = dataUri;
      }
    });
  }
  return thumbnails;
}

// Fill the channel into the "{}"-templated rtsp_url_suffix (e.g. "/Streaming/Channels/{}01")
// so Dashcam can skip path discovery. Null when no suffix is set.
function resolveRtspPath(dvr, channelNumber) {
  const suffix = dvr?.rtsp_url_suffix;
  if (typeof suffix !== "string" || !suffix.trim()) return null;
  return suffix.replaceAll("{}", String(channelNumber));
}

// Run `fn` over `items` with at most `limit` in flight; preserves input order.
async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const idx = next;
      next += 1;
      results[idx] = await fn(items[idx], idx);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// Grabs one frame per channel from Dashcam /screenshot. Returns screenshots as
// `[channel, null, data_uri]`; drops failed channels, throws only when all fail.
export async function scanInstallationChannels({ installation, dvr, channelNumbers }) {
  const blocker = manualScanBlocker(installation, dvr);
  if (blocker) throw new Error(blocker);
  if (!Array.isArray(channelNumbers) || channelNumbers.length === 0) {
    throw new Error("No channels selected.");
  }
  if (channelNumbers.length > MAX_CHANNELS) {
    throw new Error(`Cannot scan more than ${MAX_CHANNELS} channels at once.`);
  }

  const isCloud = Boolean(installation?.is_cloud);
  const videoRecorder = installationToVideoRecorderShape({
    dvr,
    typeName: "IP",
    inferenceHostname: isCloud ? null : installation?.server_name ?? null,
    deviceHost: isCloud ? cloudDeviceHost(installation) : null,
  });

  // Resolve the VPN tunnel base once, then reuse it for every channel grab.
  let base;
  try {
    base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference: !isCloud });
  } catch (err) {
    if (isDashcamRequestCancelled(err)) throw err;
    throw new Error(mapDashcamHttpError(err));
  }

  const grabOne = async (channelNumber) => {
    try {
      const payload = buildScreenshotRequestBody(base, videoRecorder, {
        localInference: !isCloud,
        channel: channelNumber,
        streamType: "main",
        rtspPath: resolveRtspPath(dvr, channelNumber),
      });
      const resp = await postScreenshot(payload);
      const b64 = resp?.data?.image_base64;
      if (typeof b64 !== "string" || !b64) return { channel: channelNumber, error: new Error("No frame returned.") };
      return { channel: channelNumber, dataUri: `data:image/jpeg;base64,${b64}` };
    } catch (err) {
      return { channel: channelNumber, error: err };
    }
  };

  const outcomes = await mapWithConcurrency(channelNumbers, SCREENSHOT_CONCURRENCY, grabOne);

  const cancelled = outcomes.find((o) => o.error && isDashcamRequestCancelled(o.error));
  if (cancelled) throw cancelled.error;

  const screenshots = outcomes.filter((o) => o.dataUri).map((o) => [o.channel, null, o.dataUri]);
  if (screenshots.length === 0) {
    const lastError = outcomes.filter((o) => o.error).pop()?.error ?? null;
    throw new Error(lastError ? mapDashcamHttpError(lastError) : "Failed to fetch screenshots.");
  }
  return { channels: channelNumbers, screenshots, screenshotsPolygon: [] };
}
